import { ChangeEvent, CSSProperties, InputHTMLAttributes } from 'react';

import { cn } from '../../helpers/classBuilders';

const DEFAULT_ACTIVE_COLOR = 'var(--color-brand-primary)';
const DEFAULT_SECONDARY_COLOR = 'var(--color-gray-500, #6b7280)';

interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  id: string;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  /** 'dual' keeps the track colored on both sides — for A/B mode switches, not on/off. */
  variant?: 'default' | 'dual';
  /** Track color when checked. Defaults to brand primary. */
  activeColor?: string;
  /** dual only: track color when unchecked. Defaults to a neutral gray. */
  secondaryColor?: string;
  /** Extra classes for the track. */
  className?: string;
}

/**
 * Controlled on/off switch built on a hidden native checkbox (role="switch").
 * Track colors are runtime props, hence the inline CSS custom properties.
 */
export const Switch = ({
  id,
  checked,
  onChange,
  disabled = false,
  variant = 'default',
  activeColor = DEFAULT_ACTIVE_COLOR,
  secondaryColor = DEFAULT_SECONDARY_COLOR,
  className = '',
  ...rest
}: SwitchProps) => {
  const trackColors = {
    '--switch-active-color': activeColor,
    '--switch-secondary-color': secondaryColor,
  } as CSSProperties;

  return (
    <label
      htmlFor={id}
      style={trackColors}
      className={cn(
        'c-Switch inline-flex h-6 w-11 shrink-0 cursor-pointer select-none items-center rounded-full p-0.5 transition-colors duration-300',
        // Mutually exclusive on purpose — a base bg would clash with the checked color.
        {
          'bg-(--switch-active-color)': checked,
          'bg-(--switch-secondary-color)': !checked && variant === 'dual',
          'bg-gray-500': !checked && variant === 'default',
          'cursor-not-allowed opacity-50': disabled,
        },
        'has-focus-visible:focus-ring-form',
        className,
      )}
    >
      <input
        className="sr-only"
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-checked={checked}
        aria-disabled={disabled}
        {...rest}
      />
      <span
        className={cn(
          'block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-300',
          {
            'translate-x-5': checked,
          },
        )}
        aria-hidden="true"
      />
    </label>
  );
};
